import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Resets scroll position on every route change.
 *
 * SPA navigations don't trigger a browser scroll reset the way full page
 * loads do, so a user landing on /checkout after scrolling down /cart would
 * start partway down the page. Drop this inside the Router to restore the
 * expected "new page starts at the top" behaviour. Safe with HashRouter
 * because the hash there is the route, not an in-page anchor target.
 *
 * When the app is mounted inside a larger WordPress page, scrolling all the
 * way to (0, 0) yanks the user past the WP theme's header and chrome on
 * every route change. Pass `selector` to scroll to the top of the app's
 * root container instead, keeping the surrounding theme stable.
 */
export type ScrollToTopProps = {
  /**
   * CSS selector for the element whose top should align with the viewport
   * on route change. Omit to scroll the window to (0, 0) — the original
   * behaviour, fine for standalone apps.
   */
  selector?: string
}

export function ScrollToTop({ selector }: ScrollToTopProps = {}) {
  const { pathname } = useLocation()
  useEffect(() => {
    if (selector) {
      const el = document.querySelector(selector)
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY
        window.scrollTo(0, top)
        return
      }
    }
    window.scrollTo(0, 0)
  }, [pathname, selector])
  return null
}
